/* Copyright (c) 2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, timer, from } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { NvHttpEndpoint, NvHttpEndpointFactoryService, NvRequestOptions } from 'nv-angular-http-endpoint';
import { LoggingService, Logger } from 'crimson';
import { Configuration, PublicLearnData, Topic } from '../../shared';

const DEFAULT_PUBLIC_LEARN_DATA: PublicLearnData = { data: [] };

@Injectable({
    providedIn: 'root'
})
export class PublicLearnArticleService {
    private logger: Logger;
    private endPoint: NvHttpEndpoint;
    private readonly RETRY_COUNT = 1;
    private publicLearnArticles = new BehaviorSubject<PublicLearnData>(DEFAULT_PUBLIC_LEARN_DATA);

    constructor(
        private appConfig: Configuration,
        private loggingService: LoggingService,
        private endpointFactory: NvHttpEndpointFactoryService
    ) {
        this.logger = this.loggingService.getLogger('LearnCardService');
    }

    init() {
        this.logger.info('LearnCardService Service Initialization - Success');
    }

    private createEndpoint(): NvHttpEndpoint {
        return this.endpointFactory.createEndpoint({
            config: {
                defaultTimeout: 10000,
                defaultTimeBetweenRetries: 5000,
                defaultRetries: 1
            }
        });
    }

    private fetchArticles(): Observable<PublicLearnData> {
        this.endPoint = this.createEndpoint();

        // CORS exceptions have to be made at the learn article servers.  Current
        // exceptions are for:
        // Production: https://developer.geforcenow.com/learn-assets
        //   Exceptions: https://gfn-playtest-website-stg.s3.us-west-1.amazonaws.com
        // Staging: https://game-developer.staging.developer.nvidiagrid.net/api/2/organizations/public-learn-articles
        //   Exceptions: https://playtest.geforcenow.com/

        // Force and OPTIONS preflight request
        const requestOptions: NvRequestOptions = {
            headers: {
                'X-Playtest-Portal': 'true'
            }
        };
        return this.endPoint.get<any>(this.appConfig.vdkPlaytestConfig.publicLearnArticlesUrl, requestOptions).pipe(
            take(1),
            map(response => {
                this.logger.info('Fetch public learn articles - Success');
                return response as PublicLearnData;
            }),
            catchError(error => {
                this.logger.error('Fetch public learn articles - Error', error);
                return of(DEFAULT_PUBLIC_LEARN_DATA);
            })
        );
    }

    public getArticles(forceFetch = false): Observable<PublicLearnData> {
        if (forceFetch || this.publicLearnArticles.getValue().data.length === 0) {
            return this.fetchArticles().pipe(
                map(articles => {
                    this.publicLearnArticles.next(articles);
                    return articles;
                })
            );
        }
        return this.publicLearnArticles.asObservable();
    }

    public getArticlesValue(): PublicLearnData {
        return this.publicLearnArticles.getValue();
    }

    public getTopicsForTag(tags: string[]): Topic[] {
        const articles = this.publicLearnArticles.getValue();
        const topics = [];
        if (articles !== null) {
            articles.data.forEach(category => {
                category.topics.forEach(topic => {
                    tags.forEach(tag => {
                        if (topic.playtestPortalTags && topic.playtestPortalTags.includes(tag)) {
                            topics.push(topic);
                        }
                    });
                });
            });
        }
        return topics;
    }
}
